import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { getStaffOrders, requeueOrderPrintJob, updateStaffOrderStatus } from "Api/apiService";

const STATUS_OPTIONS = [
  "received",
  "preparing",
  "ready",
  "picked",
  "cancelled",
];

const normalizeOrderStatusForUi = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "completed") {
    return "picked";
  }
  return normalized;
};

const STATUS_COLORS = {
  pending_payment: "yellow",
  received: "green",
  preparing: "purple",
  ready: "green",
  picked: "teal",
  completed: "teal",
  cancelled: "red",
};

const PRINT_STATUS_COLORS = {
  not_queued: "gray",
  queued: "yellow",
  printed: "green",
  failed: "red",
  cancelled: "red",
};

const formatOrderStatusLabel = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "pending_payment") {
    return "Awaiting Payment";
  }
  if (normalized === "received") {
    return "New Order";
  }
  if (normalized === "preparing") {
    return "In Kitchen";
  }
  if (normalized === "ready") {
    return "Ready for Pickup";
  }
  if (normalized === "picked") {
    return "Picked Up";
  }
  if (normalized === "completed") {
    return "Picked Up";
  }
  if (normalized === "cancelled") {
    return "Cancelled";
  }
  if (!normalized) {
    return "Unknown";
  }
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatPaymentMethod = (order) => {
  const provider = String(order?.payment?.provider || "").toLowerCase();
  if (provider === "paypal") {
    return "PayPal";
  }
  if (provider === "stripe") {
    return "Card";
  }
  return "Unknown";
};

const getPaymentStatusMeta = (order) => {
  const status = String(order?.paymentStatus || order?.payment?.status || "").toLowerCase();
  if (status === "paid") {
    return { label: "Paid", colorScheme: "green" };
  }
  return { label: "Not Paid", colorScheme: "red" };
};

const formatPrintStatusLabel = (status) => {
  const normalized = String(status || "not_queued").toLowerCase();
  if (normalized === "not_queued") {
    return "Not Queued";
  }
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getPrintStatusMeta = (order) => {
  const status = String(order?.printStatus || "not_queued").toLowerCase();
  return {
    label: formatPrintStatusLabel(status),
    colorScheme: PRINT_STATUS_COLORS[status] || "gray",
  };
};

export default function Orders() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [savingOrder, setSavingOrder] = useState("");
  const [printingOrder, setPrintingOrder] = useState("");
  const [pendingStatusByOrder, setPendingStatusByOrder] = useState({});
  const [selectedOrderNumber, setSelectedOrderNumber] = useState("");

  const headingColor = useColorModeValue("navy.800", "white");
  const metaColor = useColorModeValue("secondaryGray.700", "secondaryGray.500");
  const customerNameColor = useColorModeValue("navy.700", "white");
  const cardHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const itemTextColor = useColorModeValue("navy.800", "white");
  const noteBoxBg = useColorModeValue("gray.50", "whiteAlpha.80");
  const noteBorderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const noteLabelColor = useColorModeValue("gray.600", "gray.300");
  const noteTextColor = useColorModeValue("gray.700", "gray.200");

  const fetchOrders = async (status = statusFilter) => {
    setLoading(true);
    try {
      const response = await getStaffOrders(status);
      setOrders(response.data?.orders || []);
    } catch (error) {
      toast({
        title: "Failed to load orders",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedOrders = useMemo(() => {
    return [...orders].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const normalizedFilter = String(statusFilter || "").trim().toLowerCase();
    if (!normalizedFilter) {
      return sortedOrders;
    }
    return sortedOrders.filter((order) => {
      const normalizedOrderStatus = normalizeOrderStatusForUi(order?.status);
      return normalizedOrderStatus === normalizedFilter;
    });
  }, [sortedOrders, statusFilter]);

  const selectedOrder = useMemo(
    () => sortedOrders.find((order) => order.orderNumber === selectedOrderNumber),
    [sortedOrders, selectedOrderNumber]
  );

  const patchOrderStatus = async (order, nextStatus, successLabel = "Order updated") => {
    if (!order || nextStatus === order.status) {
      return;
    }

    setSavingOrder(order.orderNumber);
    try {
      const response = await updateStaffOrderStatus(order.orderNumber, nextStatus);
      const updated = response.data?.order;
      const finalStatus = updated?.status || nextStatus;

      setOrders((prev) =>
        prev.map((item) =>
          item.orderNumber === order.orderNumber
            ? { ...item, status: finalStatus }
            : item
        )
      );
      setPendingStatusByOrder((prev) => ({
        ...prev,
        [order.orderNumber]: finalStatus,
      }));

      toast({
        title: successLabel,
        description: `${order.orderNumber} set to ${formatOrderStatusLabel(finalStatus)}`,
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSavingOrder("");
    }
  };

  const handleChangeStatus = async (order) => {
    const nextStatus = pendingStatusByOrder[order.orderNumber] || order.status;
    await patchOrderStatus(order, nextStatus, "Order updated");
  };

  const handleMarkPicked = async (order) => {
    await patchOrderStatus(order, "picked", "Marked as picked");
  };

  const handlePrintOrder = async (order) => {
    if (!order) {
      return;
    }

    setPrintingOrder(order.orderNumber);
    try {
      await requeueOrderPrintJob(order.orderNumber);
      setOrders((prev) =>
        prev.map((item) =>
          item.orderNumber === order.orderNumber
            ? { ...item, printStatus: "queued" }
            : item
        )
      );

      toast({
        title: "Print queued",
        description: `${order.orderNumber} sent to printer queue.`,
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Print failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setPrintingOrder("");
    }
  };

  const openOrderModal = (order) => {
    setSelectedOrderNumber(order.orderNumber);
    setPendingStatusByOrder((prev) => ({
      ...prev,
      [order.orderNumber]: prev[order.orderNumber] || order.status,
    }));
    onOpen();
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid columns={{ base: 1 }} spacing="20px">
        <Card px="24px" py="20px">
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "stretch", md: "center" }}
            gap="12px"
          >
            <Box>
              <Text fontSize="xl" fontWeight="700" color={headingColor}>
                Incoming Orders
              </Text>
              <Text color="secondaryGray.600" fontSize="sm">
                Tap any card to open full details and actions.
              </Text>
            </Box>
            <HStack spacing="10px">
              <Select
                w={{ base: "full", md: "220px" }}
                value={statusFilter}
                onChange={(e) => {
                  const nextStatus = e.target.value;
                  setStatusFilter(nextStatus);
                  fetchOrders(nextStatus);
                }}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {formatOrderStatusLabel(status)}
                  </option>
                ))}
              </Select>
              <Button colorScheme="blue" onClick={() => fetchOrders(statusFilter)}>
                Refresh
              </Button>
            </HStack>
          </Flex>
        </Card>

        {loading ? (
          <Card px="24px" py="36px">
            <Flex align="center" justify="center" gap="10px">
              <Spinner />
              <Text>Loading orders...</Text>
            </Flex>
          </Card>
        ) : filteredOrders.length === 0 ? (
          <Card px="24px" py="36px">
            <Text>No orders found for this filter.</Text>
          </Card>
        ) : (
          filteredOrders.map((order) => {
            const customerName = order.customer?.fullName || "Guest";
            const paymentStatusMeta = getPaymentStatusMeta(order);
            const printStatusMeta = getPrintStatusMeta(order);
            const previewItems = (order.items || []).slice(0, 2);
            const hiddenItemsCount = Math.max((order.items || []).length - previewItems.length, 0);

            return (
              <Card
                key={order.orderNumber}
                px="22px"
                py="18px"
                cursor="pointer"
                transition="all 0.15s ease"
                _hover={{ transform: "translateY(-1px)", bg: cardHoverBg }}
                onClick={() => openOrderModal(order)}
              >
                <Flex justify="space-between" align="center" wrap="wrap" gap="10px">
                  <Stack spacing="3px">
                    <Text fontSize="lg" fontWeight="800" letterSpacing="0.2px" color={headingColor}>
                      {order.orderNumber}
                    </Text>
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="900" lineHeight="1" color={customerNameColor}>
                      {customerName}
                    </Text>
                    <Text fontSize="sm" color={metaColor}>
                      {new Date(order.createdAt).toLocaleString()}
                    </Text>
                    {previewItems.map((item, idx) => (
                      <Box key={`${order.orderNumber}-preview-${idx}`}>
                        <Text
                          fontSize="md"
                          fontWeight="700"
                          color={itemTextColor}
                          noOfLines={1}
                        >
                          {item.quantity} x {item.name}
                        </Text>
                        {item.note ? (
                          <Box
                            mt="4px"
                            pl="8px"
                            pr="8px"
                            py="5px"
                            borderRadius="8px"
                            bg={noteBoxBg}
                            borderLeftWidth="3px"
                            borderLeftColor={noteBorderColor}
                          >
                            <Text
                              fontSize="10px"
                              fontWeight="700"
                              textTransform="uppercase"
                              letterSpacing="0.3px"
                              color={noteLabelColor}
                              mb="1px"
                            >
                              Kitchen Note
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight="500"
                              color={noteTextColor}
                              noOfLines={2}
                            >
                              {item.note}
                            </Text>
                          </Box>
                        ) : null}
                      </Box>
                    ))}
                    {hiddenItemsCount > 0 ? (
                      <Text fontSize="sm" color={metaColor}>
                        +{hiddenItemsCount} more
                      </Text>
                    ) : null}
                  </Stack>
                  <Stack spacing="8px" align="flex-end">
                    <Badge colorScheme={STATUS_COLORS[order.status] || "gray"} px="10px" py="6px" borderRadius="full">
                      {formatOrderStatusLabel(order.status)}
                    </Badge>
                    <Badge colorScheme={paymentStatusMeta.colorScheme} variant="subtle" px="10px" py="4px" borderRadius="full">
                      {paymentStatusMeta.label}
                    </Badge>
                    {order.printStatus === "failed" && order.printLastError ? (
                      <Tooltip label={order.printLastError} hasArrow placement="top">
                        <Badge colorScheme={printStatusMeta.colorScheme} variant="subtle" px="10px" py="4px" borderRadius="full" cursor="help">
                          {printStatusMeta.label}
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Badge colorScheme={printStatusMeta.colorScheme} variant="subtle" px="10px" py="4px" borderRadius="full">
                        {printStatusMeta.label}
                      </Badge>
                    )}
                    <Badge colorScheme="orange" variant="subtle" px="10px" py="4px" borderRadius="full">
                      {formatPaymentMethod(order)}
                    </Badge>
                    <Text fontWeight="800" fontSize="xl" color={headingColor}>
                      ${Number(order.pricing?.total || 0).toFixed(2)}
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      isLoading={printingOrder === order.orderNumber}
                      isDisabled={order.paymentStatus !== "paid" || order.status === "cancelled"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintOrder(order);
                      }}
                    >
                      Print
                    </Button>
                  </Stack>
                </Flex>
              </Card>
            );
          })
        )}
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Stack spacing="2px">
              <Text fontSize="lg" fontWeight="800">
                {selectedOrder?.orderNumber || "Order"}
              </Text>
              <Text fontSize="2xl" fontWeight="900" lineHeight="1.1" color={customerNameColor}>
                {selectedOrder?.customer?.fullName || "Guest"}
              </Text>
            </Stack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="24px">
            {!selectedOrder ? (
              <Text color={metaColor}>Order details are not available.</Text>
            ) : (
              <Stack spacing="14px">
                <Flex justify="space-between" align="center" wrap="wrap" gap="10px">
                  <HStack spacing="8px" align="center">
                    <Badge colorScheme={STATUS_COLORS[selectedOrder.status] || "gray"} px="10px" py="6px" borderRadius="full">
                      {formatOrderStatusLabel(selectedOrder.status)}
                    </Badge>
                    <Badge
                      colorScheme={getPaymentStatusMeta(selectedOrder).colorScheme}
                      variant="subtle"
                      px="10px"
                      py="6px"
                      borderRadius="full"
                    >
                      {getPaymentStatusMeta(selectedOrder).label}
                    </Badge>
                    {selectedOrder.printStatus === "failed" && selectedOrder.printLastError ? (
                      <Tooltip label={selectedOrder.printLastError} hasArrow placement="top">
                        <Badge
                          colorScheme={getPrintStatusMeta(selectedOrder).colorScheme}
                          variant="subtle"
                          px="10px"
                          py="6px"
                          borderRadius="full"
                          cursor="help"
                        >
                          {getPrintStatusMeta(selectedOrder).label}
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Badge
                        colorScheme={getPrintStatusMeta(selectedOrder).colorScheme}
                        variant="subtle"
                        px="10px"
                        py="6px"
                        borderRadius="full"
                      >
                        {getPrintStatusMeta(selectedOrder).label}
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="sm" color={metaColor}>
                    {new Date(selectedOrder.createdAt).toLocaleString()}
                  </Text>
                </Flex>

                <Text fontSize="sm" color={metaColor}>
                  {selectedOrder.customer?.phoneNumber || "-"} • {selectedOrder.customer?.emailAddress || "-"}
                </Text>
                <Text fontSize="sm" color={metaColor}>
                  Payment: {formatPaymentMethod(selectedOrder)}
                </Text>

                <Divider />

                <Box>
                  <Text fontWeight="700" mb="6px" color={headingColor}>
                    Items
                  </Text>
                  <Stack spacing="6px">
                    {(selectedOrder.items || []).map((item, idx) => (
                      <Box key={`${selectedOrder.orderNumber}-${idx}`}>
                        <Text fontSize="lg" fontWeight="700" color={itemTextColor}>
                          {item.quantity} x {item.name}
                        </Text>
                        {item.note ? (
                          <Box
                            mt="6px"
                            pl="10px"
                            pr="10px"
                            py="7px"
                            borderRadius="10px"
                            bg={noteBoxBg}
                            borderLeftWidth="4px"
                            borderLeftColor={noteBorderColor}
                          >
                            <Text
                              fontSize="11px"
                              fontWeight="700"
                              textTransform="uppercase"
                              letterSpacing="0.3px"
                              color={noteLabelColor}
                              mb="2px"
                            >
                              Kitchen Note
                            </Text>
                            <Text fontSize="md" fontWeight="500" color={noteTextColor}>
                              {item.note}
                            </Text>
                          </Box>
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Flex justify="space-between" align="center" wrap="wrap" gap="12px">
                  <Text fontWeight="800" fontSize="2xl" color={headingColor}>
                    Total: ${Number(selectedOrder.pricing?.total || 0).toFixed(2)}
                  </Text>
                </Flex>

                <Divider />

                <HStack spacing="8px" align="center" wrap="wrap">
                  <Select
                    w={{ base: "100%", md: "200px" }}
                    size="sm"
                    value={normalizeOrderStatusForUi(
                      pendingStatusByOrder[selectedOrder.orderNumber] || selectedOrder.status
                    )}
                    onChange={(e) =>
                      setPendingStatusByOrder((prev) => ({
                        ...prev,
                        [selectedOrder.orderNumber]: e.target.value,
                      }))
                    }
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {formatOrderStatusLabel(status)}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    colorScheme="green"
                    isLoading={savingOrder === selectedOrder.orderNumber}
                    onClick={() => handleChangeStatus(selectedOrder)}
                  >
                    Update
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="teal"
                    variant="outline"
                    isLoading={savingOrder === selectedOrder.orderNumber}
                    onClick={() => handleMarkPicked(selectedOrder)}
                  >
                    Picked
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    isLoading={printingOrder === selectedOrder.orderNumber}
                    isDisabled={selectedOrder.paymentStatus !== "paid" || selectedOrder.status === "cancelled"}
                    onClick={() => handlePrintOrder(selectedOrder)}
                  >
                    Print
                  </Button>
                </HStack>
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
